import { useState, useCallback } from 'react';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import type { Publisher } from 'types/publisher';
import {
  updatePublisher,
  submitPublisher,
  approvePublisher,
  rejectPublisher,
  archivePublisher,
  type UpdatePublisherData,
} from '../api/marketplaceApi';

export interface UsePublisherManageResult {
  saving: boolean;
  submitting: boolean;
  approving: boolean;
  rejecting: boolean;
  archiving: boolean;
  save: (slug: string, data: UpdatePublisherData) => Promise<Publisher | null>;
  submit: (slug: string) => Promise<Publisher | null>;
  approve: (slug: string) => Promise<Publisher | null>;
  reject: (slug: string, note: string) => Promise<Publisher | null>;
  archive: (slug: string) => Promise<Publisher | null>;
}

export function usePublisherManage(): UsePublisherManageResult {
  const apiClients = useApiClientsOrNull();

  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const save = useCallback(
    async (slug: string, data: UpdatePublisherData): Promise<Publisher | null> => {
      if (!apiClients?.schemaClient) return null;
      setSaving(true);
      try {
        const res = await updatePublisher(apiClients.schemaClient, slug, data);
        return res.publisher;
      } finally {
        setSaving(false);
      }
    },
    [apiClients]
  );

  const submit = useCallback(
    async (slug: string): Promise<Publisher | null> => {
      if (!apiClients?.schemaClient) return null;
      setSubmitting(true);
      try {
        const res = await submitPublisher(apiClients.schemaClient, slug);
        return res.publisher;
      } finally {
        setSubmitting(false);
      }
    },
    [apiClients]
  );

  const approve = useCallback(
    async (slug: string): Promise<Publisher | null> => {
      if (!apiClients?.schemaClient) return null;
      setApproving(true);
      try {
        const res = await approvePublisher(apiClients.schemaClient, slug);
        return res.publisher;
      } finally {
        setApproving(false);
      }
    },
    [apiClients]
  );

  const reject = useCallback(
    async (slug: string, note: string): Promise<Publisher | null> => {
      if (!apiClients?.schemaClient) return null;
      setRejecting(true);
      try {
        const res = await rejectPublisher(apiClients.schemaClient, slug, note);
        return res.publisher;
      } finally {
        setRejecting(false);
      }
    },
    [apiClients]
  );

  const archive = useCallback(
    async (slug: string): Promise<Publisher | null> => {
      if (!apiClients?.schemaClient) return null;
      setArchiving(true);
      try {
        const res = await archivePublisher(apiClients.schemaClient, slug);
        return res.publisher;
      } finally {
        setArchiving(false);
      }
    },
    [apiClients]
  );

  return { saving, submitting, approving, rejecting, archiving, save, submit, approve, reject, archive };
}
